import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  eagerLoadNodesExperimentOnlyList,
  eagerLoadNodesList,
  findNodeAndExpandParents,
  LayersContainerContext,
} from './utils';
import { TreeService } from 'business/services/TreeService';
import { TreeControlDefaultState, TreeControlState } from 'components/explorer';

type UseSetSelectedNodeEffectProps = {
  treeControlState: TreeControlState;
  setTreeControlState: React.Dispatch<React.SetStateAction<TreeControlState>>;
  treeNodeId: number;
};

const treeService = new TreeService();
export const useSetSelectedNodeEffect = (props: UseSetSelectedNodeEffectProps) => {
  const { treeControlState, setTreeControlState, treeNodeId } = props;
  const selectionAppliedRef = useRef(false);

  useLayoutEffect(() => {
    if (selectionAppliedRef.current || treeControlState.treenodes.length === 0) {
      return;
    }

    treeService.getParentIdByTreeNodeId(treeNodeId).then((parentId) => {
      const { updatedNodes, foundNode } = findNodeAndExpandParents(
        treeControlState.treenodes,
        parentId,
      );

      if (!foundNode) {
        return;
      }

      setTreeControlState((prev) => ({
        ...prev,
        treenodes: updatedNodes,
        selectedExplorerNode: foundNode,
      }));
      selectionAppliedRef.current = true;
    });
  }, [treeControlState]);
};

export const useEagerLoadTreeNodes = () => {
  const [treeControlState, setTreeControlState] = useState<TreeControlState>({
    ...TreeControlDefaultState,
  });
  const isScreenRoute = window.location.pathname.includes('/screen');

  const allowedNodeTypes = isScreenRoute
    ? eagerLoadNodesList
    : [...eagerLoadNodesList, ...eagerLoadNodesExperimentOnlyList];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setTreeControlState((prev) => ({ ...prev, isLoading: true }));
        const data = await treeService.getAllTreeNodesService(allowedNodeTypes);
        setTreeControlState((prev) => ({ ...prev, treenodes: data }));
      } finally {
        setTreeControlState((prev) => ({ ...prev, isLoading: false }));
      }
    };

    fetchData();
  }, []);

  return { treeControlState, setTreeControlState };
};

export const useLayersContainerContext = () => {
  const context = useContext(LayersContainerContext);
  if (!context) {
    return;
  }
  return context;
};
